import { memo, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { CheckBox, SelectBox } from "devextreme-react";
import { LoadPanel } from "devextreme-react/load-panel";
import {
  Chart,
  CommonSeriesSettings,
  Export,
  SeriesTemplate,
  Legend,
  ValueAxis,
  Point,
  ArgumentAxis,
  Tooltip,
  ZoomAndPan,
  Margin,
  Series,
} from "devextreme-react/chart";
import { useFetch } from "src/hooks/useFetch";
import { SpatialRun } from "../types";

interface CycleProgressResponse {
  runID: string;
  metric: string;
  cycles: CycleDataPoint[];
}

interface CycleDataPoint {
  cycle: number;
  batch: string;
  q30?: number;
  averageQScore?: number;
  green?: number;
  red?: number;
  a?: number;
  c?: number;
  g?: number;
  t?: number;
  ch1?: number;
  ch2?: number;
  ch3?: number;
  ch4?: number;
}

type CytoCycleProgressProps = {
  run: SpatialRun;
};

const metricOptions = [
  { value: "q30", label: "Q30", field: "q30", isPercentage: true },
  { value: "averageQScore", label: "Average Q Score", field: "averageQScore", isPercentage: false },
  { value: "zScores", label: "Z Scores (Green/Red)", field: null, isPercentage: false },
  { value: "baseComposition", label: "Base Composition", field: null, isPercentage: true },
  { value: "phasing", label: "Phasing", field: null, isPercentage: true },
  { value: "prephasing", label: "Prephasing", field: null, isPercentage: true },
  { value: "intensity", label: "Intensity", field: null, isPercentage: false },
  { value: "fwhm", label: "FWHM", field: null, isPercentage: false },
];

// For multi-series metrics, define the sub-fields
const getSubFields = (metric: string): { field: string; label: string }[] => {
  switch (metric) {
    case "zScores":
      return [
        { field: "green", label: "Green" },
        { field: "red", label: "Red" },
      ];
    case "baseComposition":
      return [
        { field: "a", label: "A" },
        { field: "c", label: "C" },
        { field: "g", label: "G" },
        { field: "t", label: "T" },
      ];
    case "phasing":
    case "prephasing":
    case "intensity":
    case "fwhm":
      return [
        { field: "ch1", label: "Ch1" },
        { field: "ch2", label: "Ch2" },
        { field: "ch3", label: "Ch3" },
        { field: "ch4", label: "Ch4" },
      ];
    default:
      return [];
  }
};

const getYAxisTitle = (metric: string): string => {
  switch (metric) {
    case "q30":
      return "Q30 (%)";
    case "averageQScore":
      return "Average Q Score";
    case "zScores":
      return "Z Score";
    case "baseComposition":
      return "Base Composition (%)";
    case "phasing":
      return "Phasing (%)";
    case "prephasing":
      return "Prephasing (%)";
    case "intensity":
      return "Intensity";
    case "fwhm":
      return "FWHM";
    default:
      return "";
  }
};

const getDefaultRange = (metric: string): [number, number] | null => {
  switch (metric) {
    case "q30":
    case "baseComposition":
      return [0, 100];
    case "zScores":
      return [-3, 3];
    case "phasing":
    case "prephasing":
      return [0, 2];
    default:
      return null;
  }
};

/**
 * Instrument NaN is stored as -999. Invalid intensity is an int16 sentinel
 * around -32K (-32768). Null breaks the line instead of plotting either one.
 * Real scores on this chart stay well above -32000, including negative Z scores.
 */
export const toChartValue = (
  raw: number | undefined | null,
  multiplier: number
): number | null => {
  if (raw == null || Number.isNaN(raw) || raw === -999 || raw <= -32000) return null;
  return raw * multiplier;
};

const CytoCycleProgress = memo(({ run }: CytoCycleProgressProps) => {
  const chart = useRef<any>();
  const [metric, setMetric] = useState("q30");
  const [plotData, setPlotData] = useState<any[]>([]);
  const [batches, setBatches] = useState<string[]>([]);
  const [showAbsoluteCycle, setShowAbsoluteCycle] = useState(true);

  const {
    data: rawData,
    isLoading,
  } = useFetch(
    `/spatialRun/${run?.runID}/cycleProgress/${metric}`,
    !!run?.runID && !!metric
  );

  const metricConfig = useMemo(
    () => metricOptions.find((m) => m.value === metric),
    [metric]
  );

  const subFields = useMemo(() => getSubFields(metric), [metric]);
  const isSingleFieldMetric = subFields.length === 0;

  useEffect(() => {
    if (!rawData) {
      setPlotData([]);
      setBatches([]);
      return;
    }

    const response = rawData as CycleProgressResponse;
    if (!response.cycles || response.cycles.length === 0) {
      setPlotData([]);
      setBatches([]);
      return;
    }

    // Extract unique batches (sorted)
    const uniqueBatches = Array.from(new Set(response.cycles.map((d) => d.batch))).sort();
    setBatches(uniqueBatches);

    // Calculate absolute cycle offsets per batch
    // First, find max cycle per batch
    const maxCyclePerBatch: Record<string, number> = {};
    response.cycles.forEach((d) => {
      if (!maxCyclePerBatch[d.batch] || d.cycle > maxCyclePerBatch[d.batch]) {
        maxCyclePerBatch[d.batch] = d.cycle;
      }
    });

    // Calculate cumulative offset for each batch
    const batchOffset: Record<string, number> = {};
    let cumulativeOffset = 0;
    uniqueBatches.forEach((batch) => {
      batchOffset[batch] = cumulativeOffset;
      cumulativeOffset += maxCyclePerBatch[batch] || 0;
    });

    const isPercentage = metricConfig?.isPercentage || false;
    const multiplier = isPercentage ? 100 : 1;

    if (isSingleFieldMetric) {
      // Single-value metric (q30, averageQScore) - use batch as series
      const valueField = metricConfig?.field || metric;
      const transformed = response.cycles.map((d) => ({
        cycle: d.cycle,
        displayCycle: showAbsoluteCycle ? d.cycle + batchOffset[d.batch] : d.cycle,
        batch: d.batch,
        value: toChartValue(
          d[valueField as keyof CycleDataPoint] as number,
          multiplier
        ),
      }));
      setPlotData(transformed);
    } else {
      // Multi-value metric - flatten into separate entries per sub-field
      // Each entry will have: cycle, seriesName, value
      const transformed: any[] = [];
      
      response.cycles.forEach((d) => {
        subFields.forEach((sf) => {
          const rawValue = d[sf.field as keyof CycleDataPoint] as number;
          transformed.push({
            cycle: d.cycle,
            displayCycle: showAbsoluteCycle ? d.cycle + batchOffset[d.batch] : d.cycle,
            batch: d.batch,
            seriesName: `${d.batch} ${sf.label}`,
            value: toChartValue(rawValue, multiplier),
          });
        });
      });
      setPlotData(transformed);
    }
  }, [rawData, metric, metricConfig, isSingleFieldMetric, subFields, showAbsoluteCycle]);

  const renderTooltip = useCallback(
    (pointInfo: any) => {
      if (pointInfo?.point?.data) {
        const data = pointInfo.point.data;
        return (
          <div>
            <strong>{pointInfo.seriesName}</strong>
            <br />
            {data.batch} Cycle {data.cycle}: {pointInfo.value?.toFixed(2)}
          </div>
        );
      }
      return <div />;
    },
    []
  );

  const yAxisTitle = getYAxisTitle(metric);
  const defaultRange = getDefaultRange(metric);

  return (
    <div style={{ marginTop: 20 }}>
      <LoadPanel
        position={{ of: "#cyto-cycle-chart" }}
        visible={isLoading}
        showIndicator={true}
        showPane={true}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 30, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 10 }}>Metric:</span>
          <SelectBox
            items={metricOptions}
            valueExpr="value"
            displayExpr="label"
            value={metric}
            onValueChanged={(e) => setMetric(e.value)}
            width={200}
          />
        </div>
        <CheckBox
          text="Show Absolute Cycle"
          value={showAbsoluteCycle}
          onValueChanged={(e) => setShowAbsoluteCycle(e.value)}
        />
      </div>

      {plotData.length > 0 && (
        <Chart
          key={`chart-${metric}-${showAbsoluteCycle}`}
          id="cyto-cycle-chart"
          ref={chart}
          height="60vh"
          dataSource={plotData}
          animation={false}
          onLegendClick={(e) => {
            const series = e.target;
            if (series.isVisible()) {
              series.hide();
            } else {
              series.show();
            }
          }}
        >
          <Margin top={0} bottom={0} left={0} right={0} />
          <CommonSeriesSettings
            argumentField="displayCycle"
            valueField="value"
            type="line"
          >
            <Point size={4} />
          </CommonSeriesSettings>

          {isSingleFieldMetric ? (
            // For single-field metrics, use batch as series grouping
            <SeriesTemplate nameField="batch" />
          ) : (
            // For multi-field metrics, use seriesName (batch + field combo)
            <SeriesTemplate nameField="seriesName" />
          )}

          <Tooltip enabled={true} contentRender={renderTooltip} />
          <ValueAxis title={yAxisTitle} />
          <ArgumentAxis title="Cycle" allowDecimals={false} />
          <Legend
            visible={true}
            position="outside"
            horizontalAlignment="center"
            verticalAlignment="bottom"
          />
          <ZoomAndPan argumentAxis="both" valueAxis="both" />
          <Export enabled={true} />
        </Chart>
      )}

      {!isLoading && plotData.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
          No data available for this metric.
        </div>
      )}
    </div>
  );
});

export default CytoCycleProgress;
